import { useMount, useUnmount } from 'ahooks';
import dayjs from 'lib/dayjs';
import useAuthShopUtils from 'hooks/business/authShops/useShopeeUtils';
import { setStopMessageHandlerState } from 'hooks/business/authShops/useStopMessageHandler';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { StandaloneAuthResult, LazadaStandaloneAuthResultBody } from 'services/api/admin';
import chatttSDK from 'services/chattt';
import { ShopeeAuthShopParams, ShopeeAuthWay } from '../components/ShopeeShopAuthWay';

const shopAuthedMapper = new Map<string, { chat: boolean; erp: boolean }>();

export type ShopeeAuthProgress = Array<Array<{ type: string; times?: string; message: string }>>;

export interface CaptchaInfo {
  visible: boolean;
  type?: number;
  phone?: string;
  email?: string;
  taskId: string;
  subTaskId: string;
}

export interface SyncShopsData {
  result: {
    chat?: boolean;
    erp?: boolean;
    chatErrorMsg?: string;
    erpErrorMsg?: string;
  };
  shopNames: string[];
}

const getTimes = () => dayjs().format('HH:mm:ss');

const useShopeeAuthShop = () => {
  const { t } = useTranslate();

  const [shopeeAuthWay, setShopeeAuthWay] = useState<ShopeeAuthWay>(ShopeeAuthWay.Official);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [syncShopsData, setSyncShopsData] = useState<
    Array<SyncShopsData | undefined> | undefined
  >();
  /** 授权结果整页替换展示（AuthResult）：携带 authorizationScope 时渲染价区裁剪提醒 */
  const [syncShopsDataError, setSyncShopsDataError] = useState<
    LazadaStandaloneAuthResultBody | undefined
  >();

  const [shopeeAuthParams, setShopeeAuthParams] = useState<ShopeeAuthShopParams>();
  const [shopeeAuthProgress, setShopeeAuthProgress] = useState<ShopeeAuthProgress>();
  const [shopeeClientAuthResult, setShopeeClientAuthResult] = useState<RPA.Shopee.AuthAgentJSON>();
  const [captchaInfo, setCaptchaInfo] = useState<CaptchaInfo>({
    visible: false,
    taskId: '',
    subTaskId: '',
  });
  const [taskId, setTaskId] = useState<string>('');

  const { posthogShopeeAuthShop } = useAuthShopUtils();

  useMount(() => {
    setStopMessageHandlerState(true);
  });
  useUnmount(() => {
    setStopMessageHandlerState(false);
  });

  const clientMessageHandler = (evt: MessageEvent) => {
    const { event: eventName, payload } = evt.data;
    if (import.meta.env.DEV) {
      if (evt.data.source) return;
      // eslint-disable-next-line no-console
      console.log('shopee auth message: ', evt.data);
    }

    switch (eventName as RPA.Shopee.DispatchToWebMessageHooks) {
      case 'shopAuth:start': {
        setSyncLoading(true);
        setShopeeAuthProgress([
          [
            {
              type: 'info',
              times: getTimes(),
              message: t('authShop.正在登录'),
            },
          ],
        ]);
        break;
      }
      case 'shopAuth:loginSuccess': {
        if (shopeeAuthProgress && shopeeAuthProgress.length) {
          const [login] = shopeeAuthProgress;
          if (shopeeAuthProgress.length === 1) {
            setShopeeAuthProgress([
              [
                ...login,
                {
                  type: 'info',
                  message: '...',
                },
                {
                  type: 'success',
                  times: getTimes(),
                  message: t('authShop.登录完成'),
                },
              ],
              [
                {
                  type: 'info',
                  times: getTimes(),
                  message: t('authShop.正在授权'),
                },
              ],
            ]);
          }
        }
        break;
      }
      case 'shopAuth:loginFail': {
        if (shopeeAuthProgress && shopeeAuthProgress.length) {
          const [login] = shopeeAuthProgress;
          setShopeeAuthProgress([
            [
              ...login,
              {
                type: 'info',
                message: '...',
              },
              {
                type: 'error',
                times: getTimes(),
                message: t('authShop.登录失败'),
              },
            ],
          ]);
        }
        break;
      }
      case 'shopAuth:chatSuccess': {
        if (shopeeAuthProgress && shopeeAuthProgress.length) {
          const [login, chat] = shopeeAuthProgress;
          setShopeeAuthProgress([
            login,
            [
              ...chat,
              {
                type: 'info',
                message: '...',
              },
              {
                type: 'success',
                times: getTimes(),
                message: t('authShop.授权完成'),
              },
            ],
            [
              {
                type: 'info',
                times: getTimes(),
                message: t('authShop.正在授权'),
              },
            ],
          ]);
        }
        break;
      }
      case 'shopAuth:chatFail': {
        if (shopeeAuthProgress && shopeeAuthProgress.length) {
          const [login, chat] = shopeeAuthProgress;
          if (chat) {
            setShopeeAuthProgress([
              login,
              [
                ...chat,
                {
                  type: 'info',
                  message: '...',
                },
                {
                  type: 'error',
                  times: getTimes(),
                  message: t('authShop.授权失败'),
                },
              ],
            ]);
          }
        }
        break;
      }
      case 'shopAuth:erpSuccess': {
        if (shopeeAuthProgress && shopeeAuthProgress.length) {
          const [login, chat, erp] = shopeeAuthProgress;
          if (chat && erp) {
            setShopeeAuthProgress([
              login,
              chat,
              [
                ...erp,
                {
                  type: 'info',
                  message: '...',
                },
                {
                  type: 'success',
                  times: getTimes(),
                  message: t('authShop.授权完成'),
                },
              ],
            ]);
          }
        }
        break;
      }
      case 'shopAuth:erpFail': {
        if (shopeeAuthProgress && shopeeAuthProgress.length) {
          const [login, chat, erp] = shopeeAuthProgress;
          if (chat && erp) {
            setShopeeAuthProgress([
              login,
              chat,
              [
                ...erp,
                {
                  type: 'info',
                  message: '...',
                },
                {
                  type: 'error',
                  times: getTimes(),
                  message: t('authShop.授权失败'),
                },
              ],
            ]);
          }
        }
        break;
      }
      case 'shopAuth:complete': {
        setSyncLoading(false);
        setShopeeClientAuthResult(payload.data[0]);
        posthogShopeeAuthShop(payload.data[0]);
        break;
      }
      case 'shopAuth:waitForOTP':
      case 'shopAuth:waitForOTPRetry':
      case 'shopAuth:waitForImageCaptcha': {
        const data: RPA.Shopee.AuthAgentJSON = payload;
        if (data) {
          const { authResult } = data;
          const subTask = authResult?.CHAT || authResult?.ERP;
          const optCodeInfo = subTask?.optCodeInfo;
          if (optCodeInfo && taskId && data.id) {
            setCaptchaInfo({
              visible: true,
              type: optCodeInfo.otp_type,
              phone: optCodeInfo.phone,
              email: optCodeInfo.email,
              taskId,
              subTaskId: data?.id,
            });
          }
        }
        break;
      }
      default:
        break;
    }
  };

  const continueSmsCaptcha = (value: RPA.Shopee.RendererEventPayloads.ContinueOTPEventPayload) => {
    if (chatttSDK?.shopee?.multiAuth) {
      chatttSDK.shopee.multiAuth('continue:otp', {
        taskId: value.taskId,
        subTaskId: value.subTaskId,
        otp: value.otp,
      });

      setCaptchaInfo({
        visible: false,
        taskId: '',
        subTaskId: '',
      });
    }
  };

  const terminateAuth = () => {
    if (chatttSDK?.shopee?.multiAuth) {
      if (taskId) {
        chatttSDK?.shopee?.multiAuth('cancel', {
          taskId,
        });
      }
      setCaptchaInfo({
        visible: false,
        taskId: '',
        subTaskId: '',
      });
      setTaskId('');
    }
  };

  const clearSyncShopsCache = () => {
    shopAuthedMapper.clear();
    setSyncShopsData(undefined);
  };

  const onAuthResult = (type: IM_API.Admin.PartnerType, value: StandaloneAuthResult) => {
    const updateMapper = (result: boolean) => {
      let o: any;
      if (type === 'CHAT') {
        o = { chat: result, chatErrorMsg: result === false ? value.errorMsg : '' };
      }
      if (type === 'ERP') {
        o = { erp: result, erpErrorMsg: result === false ? value.errorMsg : '' };
      }

      value.storeNames.forEach((shopName) => {
        const oldValue = shopAuthedMapper.get(shopName);
        if (oldValue) {
          shopAuthedMapper.set(shopName, { ...oldValue, ...o });
        } else if (o) {
          shopAuthedMapper.set(shopName, o);
        }
      });
    };

    if (value) {
      if (value.isSuccess) {
        updateMapper(true);
      } else {
        updateMapper(false);
      }
    }

    // 归类
    const group: SyncShopsData[] = [];
    for (const [key, value] of shopAuthedMapper) {
      const gItem = group.find(
        (g) => g.result?.chat === value?.chat && g.result?.erp === value?.erp,
      );
      if (gItem) {
        gItem.shopNames.push(key);
      } else {
        group.push({
          result: value,
          shopNames: [key],
        });
      }
    }
    setSyncShopsData(group);
  };

  return {
    shopeeAuthWay,
    setShopeeAuthWay,
    syncLoading,
    setSyncLoading,
    syncShopsData,
    shopeeAuthParams,
    setShopeeAuthParams,
    clientMessageHandler,
    shopeeAuthProgress,
    shopeeClientAuthResult,
    setShopeeClientAuthResult,
    captchaInfo,
    setCaptchaInfo,
    taskId,
    setTaskId,
    continueSmsCaptcha,
    terminateAuth,
    setSyncShopsData,
    syncShopsDataError,
    setSyncShopsDataError,
    clearSyncShopsCache,
    onAuthResult,
  };
};

export default useShopeeAuthShop;
